import { useHistory } from 'react-router-dom';
import { InvalidateQueryFilters, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { createEngageMessage, duplicateEngageMessage, updateEngageMessageById } from 'api/engage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';

const useLetterActions = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const history = useHistory();

    const invalidate = (options: InvalidateQueryFilters = {}) =>
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.engageMessages(accountId),
            ...options,
        });

    const createNewLetterDraftMutation = useMutation({
        mutationFn: (sendToPrintService: boolean) =>
            createEngageMessage(accountId, {
                channel: 'letter',
                settings: { send_to_print_and_mail_service: sendToPrintService },
            }),
        onSuccess: data => {
            invalidate({ type: 'all' });
            history.push(`/accounts/${accountId}/engage/letters/draft/${data.id}/edit/details`);
        },
    });

    const duplicateMessageMutation = useMutation({
        mutationFn: (messageId: number) => duplicateEngageMessage(accountId, messageId),
        onSuccess: data => {
            toast.success('Successfully duplicated letter');
            invalidate({ type: 'all' });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            history.push(`/accounts/${accountId}/engage/letters/draft/${data.id}/edit/details`);
        },
        onError: () => {
            toast.error('Failed to duplicate letter');
        },
    });

    const markLetterAsSentMutation = useMutation({
        mutationFn: (messageId: number) =>
            updateEngageMessageById(accountId, messageId, { status: 'sent', channel: 'letter' }),
        onSuccess: () => {
            toast.success('Successfully marked letter as Sent');
            invalidate({ type: 'all' });
        },
        onError: () => {
            toast.error('Failed to mark letter as Sent');
        },
    });

    const markLetterAsUnsentMutation = useMutation({
        mutationFn: (messageId: number) =>
            updateEngageMessageById(accountId, messageId, { status: 'sending', channel: 'letter' }),
        onSuccess: () => {
            toast.success('Successfully marked letter as Unsent');
            invalidate({ type: 'all' });
        },
        onError: () => {
            toast.error('Failed to mark letter as Unsent');
        },
    });

    const moveMessageBackToDraftMutation = useMutation({
        mutationFn: (messageId: number) =>
            updateEngageMessageById(accountId, messageId, { channel: 'letter', status: 'draft' }),
        onSuccess: data => {
            invalidate({ type: 'all' });
            history.push(`/accounts/${accountId}/engage/letters/draft/${data.id}/edit/compose`);
        },
    });

    return {
        createNewLetterDraftMutation,
        duplicateMessageMutation,
        markLetterAsSentMutation,
        markLetterAsUnsentMutation,
        moveMessageBackToDraftMutation,
        invalidate,
    };
};

export { useLetterActions };
